import type { ImportCommitResponse, ImportPartitionKind, ImportPreviewRow } from "./externalRegisterImportTypes";
import i18n from "@/i18n";

const PARTITIONS: ImportPartitionKind[] = ["HIGH", "MEDIUM", "LOW", "ERROR"];
export type ReportDownloadKind = ImportPartitionKind | "EXCLUDED";

export type ParsedCsv = {
  rows: string[][];
  parseErrors: Array<{ rowNumber: number; message: string }>;
};

export type PartitionDownload = {
  kind: ReportDownloadKind;
  fileName: string;
  rowCount: number;
  url: string;
};

export function fingerprintText(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `fp-${(hash >>> 0).toString(16)}`;
}

export function parseCsvText(csvText: string): ParsedCsv {
  const rows: string[][] = [];
  const parseErrors: ParsedCsv["parseErrors"] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  let rowNumber = 1;

  for (let index = 0; index < csvText.length; index += 1) {
    const char = csvText[index];
    const next = csvText[index + 1];

    if (inQuotes) {
      if (char === "\"" && next === "\"") {
        field += "\"";
        index += 1;
      } else if (char === "\"") {
        inQuotes = false;
      } else {
        field += char;
      }
      continue;
    }

    if (char === "\"") {
      if (field.length > 0) {
        parseErrors.push({ rowNumber, message: i18n.t("externalRegister.import.parse.errors.messages.unexpectedQuote") });
      }
      inQuotes = true;
      continue;
    }

    if (char === ",") {
      row.push(field);
      field = "";
      continue;
    }

    if (char === "\n" || char === "\r") {
      row.push(field);
      if (!(row.length === 1 && row[0] === "" && rows.length === 0)) {
        rows.push(row);
      }
      row = [];
      field = "";
      if (char === "\r" && next === "\n") {
        index += 1;
      }
      rowNumber += 1;
      continue;
    }

    field += char;
  }

  if (inQuotes) {
    parseErrors.push({ rowNumber, message: i18n.t("externalRegister.import.parse.errors.messages.unclosedQuote") });
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  const expectedColumns = rows.reduce((max, candidate) => Math.max(max, candidate.length), 0);
  rows.forEach((candidate, index) => {
    if (candidate.length !== expectedColumns) {
      parseErrors.push({
        rowNumber: index + 1,
        message: i18n.t("externalRegister.import.parse.errors.messages.inconsistentColumns", { expected: expectedColumns, found: candidate.length }),
      });
    }
  });

  return { rows, parseErrors };
}

export function serializeCsvRows(rows: string[][]): string {
  return rows
    .map((row) => row.map(serializeCsvField).join(","))
    .join("\n");
}

export function getCsvDataRows(rows: string[][], hasHeader: boolean): string[][] {
  return hasHeader ? rows.slice(1) : rows;
}

export function serializeCsvField(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, "\"\"")}"`;
  }
  return value;
}

export function generateClientImportId(): string {
  const randomValue = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `client-import-${randomValue}`;
}

export function buildPartitionDownloads({
  csvText,
  hasHeader,
  previewRows,
  commitResponse,
  excludedRowIds = new Set(),
}: {
  csvText: string;
  hasHeader: boolean;
  previewRows: ImportPreviewRow[];
  commitResponse?: ImportCommitResponse | null;
  excludedRowIds?: Set<string>;
}): PartitionDownload[] {
  const parsed = parseCsvText(csvText);
  const headerRows = hasHeader && parsed.rows[0] ? [parsed.rows[0]] : [];
  const finalConfidence = new Map<string, ImportPartitionKind>();

  previewRows.forEach((row) => {
    finalConfidence.set(row.rowId, row.matchConfidence);
  });

  commitResponse?.rows.forEach((row) => {
    finalConfidence.set(row.rowId, row.matchConfidence);
  });

  const rowsByPartition = new Map<ImportPartitionKind, string[][]>(
    PARTITIONS.map((partition) => [partition, [...headerRows]]),
  );
  const excludedRows = [...headerRows];

  const rowIdsByNumber = new Map(previewRows.map((row) => [row.rowNumber, row.rowId]));
  const dataStartRowNumber = hasHeader ? 2 : 1;
  getCsvDataRows(parsed.rows, hasHeader).forEach((row, index) => {
    const rowNumber = dataStartRowNumber + index;
    const rowId = rowIdsByNumber.get(rowNumber);
    if (rowId && excludedRowIds.has(rowId)) {
      excludedRows.push(row);
    }
    const partition = rowId ? finalConfidence.get(rowId) ?? "ERROR" : "ERROR";
    rowsByPartition.get(partition)?.push(row);
  });

  const partitionDownloads: PartitionDownload[] = PARTITIONS.map((kind) => {
    const partitionRows = rowsByPartition.get(kind) ?? [];
    const csv = serializeCsvRows(partitionRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    return {
      kind,
      fileName: `external-register-import-${kind}.csv`,
      rowCount: Math.max(0, partitionRows.length - headerRows.length),
      url: URL.createObjectURL(blob),
    };
  });

  const excludedCsv = serializeCsvRows(excludedRows);
  const excludedBlob = new Blob([excludedCsv], { type: "text/csv;charset=utf-8;" });
  return [
    ...partitionDownloads,
    {
      kind: "EXCLUDED",
      fileName: "external-register-import-EXCLUDED.csv",
      rowCount: Math.max(0, excludedRows.length - headerRows.length),
      url: URL.createObjectURL(excludedBlob),
    },
  ];
}

export function revokePartitionDownloads(downloads: PartitionDownload[]): void {
  downloads.forEach((download) => {
    URL.revokeObjectURL(download.url);
  });
}
